import React, { useEffect, useState } from 'react';
import { Check, Inbox, Pencil, Shield } from 'lucide-react';
import { TruncatedText } from '../../ui';
import { OutreachAction, SupplierThread } from './types';
import { clampCounter, counterSaving } from '../../../hooks/outreachActionModel';
import { formatPrice, getCurrencySymbol } from '../../../utils/currency';
import StagedTag from './StagedTag';
import { useReadOnly } from '../../../contexts/WizardAccessContext';

/**
 * The price-counter card: the analyst's number, its working, and the bound it
 * was chosen inside — with the number itself editable in place.
 *
 * The buyer approves the NUMBER, never the prose, and in two deliberate steps:
 * confirming the number, then putting the ask in the box. Nothing is sent from
 * here — the composer's send button is the only thing that reaches a supplier.
 * The floor line makes the clamp visible rather than leaving it a silent server
 * rule, and the same clamp runs here so the buyer sees their number move
 * instead of being quietly corrected after the fact.
 *
 * A viewer reads the number and the working; setting it belongs to the owner.
 */
interface CounterOfferCardProps {
  thread: SupplierThread;
  action: OutreachAction;
  /** Writes this ask into the composer with the number the buyer confirmed. */
  onStage: (approvedPrice: number) => void;
  onDismiss: () => void;
  disabled?: boolean;
  /** This ask is already in the box, waiting on the buyer's send. */
  staged?: boolean;
  /**
   * Reports the number the buyer confirmed, or null while there isn't one. An
   * unconfirmed price is never written into the box — not by this card, and
   * not by the band's "put them all in".
   */
  onConfirmPrice: (price: number | null) => void;
}

const CounterOfferCard: React.FC<CounterOfferCardProps> = ({
  thread,
  action,
  onStage,
  onDismiss,
  disabled,
  staged,
  onConfirmPrice,
}) => {
  const readOnly = useReadOnly();
  const anchor = action.anchor;
  const currency = anchor?.currency || thread.quote?.currency || 'INR';
  const [price, setPrice] = useState<number>(anchor?.price ?? 0);
  const [editing, setEditing] = useState(false);
  const [typed, setTyped] = useState<string>(String(anchor?.price ?? ''));
  const [movedNote, setMovedNote] = useState<string | null>(null);
  const [confirmed, setConfirmed] = useState(false);

  useEffect(() => {
    setPrice(anchor?.price ?? 0);
    setTyped(String(anchor?.price ?? ''));
    setEditing(false);
    setMovedNote(null);
    setConfirmed(false);
    // A different card is a different number: the band must not carry a
    // confirmation the buyer made against the price it replaced.
    onConfirmPrice(null);
  }, [action.id, anchor?.price, onConfirmPrice]);

  const holdNumber = (next: number | null) => {
    setConfirmed(next != null);
    onConfirmPrice(next);
  };

  const commit = () => {
    const parsed = Number(typed.replace(/[^0-9.]/g, ''));
    setEditing(false);
    if (!Number.isFinite(parsed) || parsed <= 0) {
      setTyped(String(price));
      return;
    }
    const { price: clamped, clamped: wasClamped } = clampCounter(parsed, {
      floor: anchor?.floor,
      ceiling: anchor?.ceiling,
    });
    setPrice(clamped);
    setTyped(String(clamped));
    // A moved number is a different number — it has to be confirmed again.
    holdNumber(null);
    setMovedNote(
      wasClamped
        ? `I moved that to ${formatPrice(clamped, currency)} — outside that range the counter stops being one.`
        : null,
    );
  };

  const saving = counterSaving(price, anchor?.ceiling, thread.quantity);

  return (
    <div className={`rounded-card border px-[12px] py-[9px] ${staged ? 'border-line bg-canvas' : 'border-robbie-border bg-surface'}`}>
      <div className="mb-s1 flex flex-wrap items-center gap-s3">
        <span className={`shrink-0 text-row-title font-bold ${staged ? 'text-ink-secondary' : 'text-ink'}`}>
          Counter at <span className="font-mono">{formatPrice(price, currency)}</span>
        </span>
        {saving != null && (
          <span className="shrink-0 rounded-[5px] border border-success-border bg-success-bg px-[7px] py-[1px] font-mono text-[10.5px] font-semibold text-success-ink">
            −{formatPrice(saving, currency)} on {thread.quantity} units
          </span>
        )}
        <TruncatedText className="ml-auto min-w-0 text-meta text-ink-secondary" text={action.rationale} />
      </div>

      <div className="mb-s2 flex flex-wrap items-center gap-s1">
        {anchor?.ceiling != null && (
          <span className="rounded-pill border border-robbie-border-soft bg-canvas px-[7px] py-[3px] text-[10.5px] text-ink-secondary">
            Their quote <span className="font-mono font-semibold text-ink">{formatPrice(anchor.ceiling, currency)}</span>
          </span>
        )}
        {anchor?.working && (
          <TruncatedText className="min-w-0 flex-1 text-[10.5px] leading-[1.45] text-ink-secondary" text={anchor.working} />
        )}
      </div>

      <div className="flex flex-wrap items-center gap-s3">
        <span className="text-meta font-semibold text-ink-secondary">Counter at</span>
        {readOnly ? (
          <span className="font-mono text-row-title font-semibold text-ink">{formatPrice(price, currency)}</span>
        ) : staged ? (
          <span className="font-mono text-row-title font-semibold text-ink-secondary">{formatPrice(price, currency)}</span>
        ) : editing ? (
          <span className="inline-flex items-center gap-s2 rounded-control border border-action bg-action-bg px-s4 py-[4px]">
            <span className="font-mono text-row-title text-action-deep">{getCurrencySymbol(currency)}</span>
            <input
              autoFocus
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              onBlur={commit}
              onKeyDown={(e) => {
                if (e.key === 'Enter') commit();
                if (e.key === 'Escape') {
                  setTyped(String(price));
                  setEditing(false);
                }
              }}
              inputMode="decimal"
              aria-label="Counter price"
              className="w-[76px] border-none bg-transparent font-mono text-row-title font-semibold text-action-deep outline-none"
            />
            <button type="button" onMouseDown={commit} aria-label="Apply this counter" className="text-action">
              <Check className="h-3 w-3" />
            </button>
          </span>
        ) : (
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="inline-flex items-center gap-s2 rounded-control border border-action-border bg-action-bg px-s4 py-[4px] hover:border-action"
          >
            <span className="font-mono text-row-title font-semibold text-action-deep">{formatPrice(price, currency)}</span>
            <Pencil className="h-3 w-3 text-action" />
          </button>
        )}

        {readOnly ? null : staged ? (
          <StagedTag />
        ) : (
          <>
            {confirmed ? (
              <span className="inline-flex items-center gap-s2 rounded-pill border border-success-border bg-success-bg px-[9px] py-[3px] text-[10.5px] font-semibold text-success-ink">
                <Check className="h-3 w-3" /> Confirmed at {formatPrice(price, currency)}
                <span aria-hidden="true" className="text-ink-muted">
                  ·
                </span>
                <button type="button" onClick={() => holdNumber(null)} className="font-semibold text-ink-secondary hover:text-ink">
                  Change
                </button>
              </span>
            ) : (
              <button
                type="button"
                onClick={() => holdNumber(price)}
                disabled={disabled || price <= 0}
                title="Confirms the number, so the only price I ever write into your box is one you set"
                className="inline-flex items-center gap-s2 rounded-control border border-action-border bg-surface px-[11px] py-[5px] text-meta font-semibold text-action-deep hover:bg-action-bg disabled:pointer-events-none disabled:opacity-50"
              >
                <Check className="h-3 w-3" /> Confirm {formatPrice(price, currency)}
              </button>
            )}
            <button
              type="button"
              onClick={() => onStage(price)}
              disabled={disabled || !confirmed}
              title={
                confirmed
                  ? "I write this counter into your box — edit it, then send when you're happy. Nothing goes out until you do."
                  : 'Confirm the number first — I won’t put a price you haven’t set in your box'
              }
              className="inline-flex items-center gap-s2 rounded-control bg-action px-[13px] py-[6px] text-body-sm font-semibold text-white hover:bg-action-hover disabled:pointer-events-none disabled:opacity-50"
            >
              <Inbox className="h-3 w-3" /> Accept &amp; put in the box
            </button>
            <button
              type="button"
              onClick={onDismiss}
              disabled={disabled}
              className="text-meta font-semibold text-ink-secondary hover:text-ink disabled:opacity-50"
            >
              Not now
            </button>
          </>
        )}

        {anchor?.floor != null && (
          <span className="ml-auto inline-flex shrink-0 items-center gap-s2 text-[10.5px] text-ink-muted">
            <Shield className="h-[11px] w-[11px]" /> I won&rsquo;t go below{' '}
            <span className="font-mono">{formatPrice(anchor.floor, currency)}</span>
          </span>
        )}
      </div>

      {movedNote && <p className="m-0 mt-s2 text-[10.5px] text-robbie-ink">{movedNote}</p>}
    </div>
  );
};

export default CounterOfferCard;
